import gql from 'graphql-tag';

import {assert} from 'Asserts';
import {ReviewLifecycleType, ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {teamHasValidNonAuthorReviewersForReviewable} from 'notebook/reviews/review-lifecycles/ReviewLifecyclesUtil';
import * as logging from 'util/Logging';

import {
  ReviewLifecycleSummaryPanelQuery,
  ReviewLifecycleSummaryPanelQueryVariables,
} from './__generated/ReviewLifecycleSummaryPanelQuery';
import {
  findSnapshotForCurrentReviewLifecycle,
  getAuthorAction,
  type ReviewChangeGraphQL,
  type ReviewerGraphQL,
  type ReviewLifecycleGraphQL,
} from './reviewBadgeDisplayState';
import {Snapshot} from './ReviewConstants';

export const REVIEW_LIFECYCLE_SUMMARY_PANEL_QUERY = gql`
  query ReviewLifecycleSummaryPanelQuery($reviewableId: StrictString!)
  @skipAudit
  @engComponent(name: NOTEBOOK) {
    reviewable(id: $reviewableId) {
      ... on ReviewableInterface {
        id
        authors {
          id
          handle
          name
          avatarUrl
        }
        review {
          id
          status
          activeReviewLifecycleVersion {
            id
            lifecycleType
            reviewStages {
              id
              name
              selectedTeam {
                id
                name
                members {
                  id
                  name
                }
              }
              allowAuthorReviewers
            }
            reviewLifecycle {
              id
              currentVersion {
                id
                name
              }
              organization {
                id
                teams {
                  id
                  name
                }
              }
            }
          }
          reviewChanges {
            id
            createdAt
            newStatus
            creator {
              ... on CreatorInterface {
                id
                name
                handle
                avatarUrl
              }
              ... on UserNode {
                id
                email
              }
            }
            previousStage {
              id
            }
            wasEsigned
            action
            message
            snapshot {
              id
              createdAt
              status
              url
            }
            wasCreatedBySnapshot
          }
          reviewers {
            id
            reviewStatus
            updatedAt
            isUserValidReviewer
            user {
              ...UserAvatarInfoGraphQL
            }
            reviewStage {
              id
              name
              action
              description
              sequentialPositionIndex
              selectedTeam {
                id
                name
                members {
                  id
                  name
                }
                adminsJson
              }
              isTeamReassignable
              allowAuthorReviewers
              allowSenderReviewer
            }
            isTeamReassignable
          }
          reviewSender {
            id
          }
        }
        reviewSource {
          id
        }
      }
      ... on EntryNode {
        id
        template {
          id
          templateCollection {
            id
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('UserAvatarInfo').fragment}
`;

type ReviewLifecycleSummaryOrInaccessibleGraphQL = ReviewLifecycleSummaryPanelQuery['reviewable'];

type ReviewableGraphQL = ExcludeInaccessible<ReviewLifecycleSummaryOrInaccessibleGraphQL>;

type ReviewGraphQL = NonNullable<ReviewableGraphQL['review']>;

type ActiveReviewLifecycleVersionGraphQL = NonNullable<ReviewGraphQL['activeReviewLifecycleVersion']>;

export interface ReviewSummaryPanelReadyState {
  state: 'READY';
  reviewable: ReviewableGraphQL;
  review: ReviewGraphQL;
  authors: ReviewableGraphQL['authors'];
  status: ReviewStatus;
  reviewChanges: Array<ReviewChangeGraphQL>;
  reviewers: Array<ReviewerGraphQL>;
  activeReviewLifecycleVersion: ActiveReviewLifecycleVersionGraphQL;
  reviewLifecycle: ReviewLifecycleGraphQL;
  lifecycleType: ReviewLifecycleType;
  reviewSenderId: string | undefined;
  hasComments: boolean;
  snapshot: Snapshot | null;
  isSelfReview: boolean;
  maybeAuthorAction: ReturnType<typeof getAuthorAction>;
  missingTeamReviewerStages: Array<string>;
}

export type ReviewSummaryPanelResult = {state: 'PENDING'} | {state: 'HIDDEN'} | ReviewSummaryPanelReadyState;

/**
 * Data controller for the review lifecycle summary panel. Owns the (network-only) panel query and the
 * derivation the panel renders from (snapshot, author action, missing team reviewers). Runs inside the
 * panel component (which only mounts when the dropdown opens), preserving the lazy, fresh-on-open fetch.
 */
export function useReviewSummaryPanel(reviewableId: string): ReviewSummaryPanelResult {
  const result = useBenchlingQuery<
    ReviewLifecycleSummaryPanelQuery,
    ReviewLifecycleSummaryPanelQueryVariables
  >(REVIEW_LIFECYCLE_SUMMARY_PANEL_QUERY, {variables: {reviewableId}, fetchPolicy: 'network-only'});

  if (result.type === QueryResultType.PENDING) {
    return {state: 'PENDING'};
  }
  if (result.type === QueryResultType.ERROR) {
    return {state: 'HIDDEN'};
  }

  const {reviewable} = result.data;

  if (isInaccessible(reviewable)) {
    logging.error('User did not have access to the reviewable item while fetching review lifecycle data');
    return {state: 'HIDDEN'};
  }

  const {review, authors} = reviewable;

  assert(
    review != null,
    'Attempting to render a ReviewLifecycleSummaryPanel for a reviewable item that does not have an attached review'
  );

  const {status, reviewChanges, reviewers, activeReviewLifecycleVersion, reviewSender} = review;
  const reviewSenderId = reviewSender ? reviewSender.id : undefined;

  const hasComments = reviewChanges.some((change) => change.message != null && change.message.trim() !== '');

  const snapshot = findSnapshotForCurrentReviewLifecycle(reviewChanges);

  assert(activeReviewLifecycleVersion != null, 'Trying to view review summary without RLC version');
  assert(reviewers != null, 'Trying to view review summary without configured reviewers');

  const {reviewLifecycle, lifecycleType} = activeReviewLifecycleVersion;
  const isSelfReview = lifecycleType === ReviewLifecycleType.SELF_REVIEW;

  const maybeAuthorAction = getAuthorAction(status, reviewLifecycle);

  const missingTeamReviewerStages: Array<string> = [];
  activeReviewLifecycleVersion.reviewStages.forEach(({name, selectedTeam, allowAuthorReviewers}) => {
    if (selectedTeam != null) {
      const memberIds = selectedTeam.members.map((member) => member.id);
      const authorIds = authors != null ? authors.map((author) => author.id) : [];
      if (!teamHasValidNonAuthorReviewersForReviewable(memberIds, authorIds) && !allowAuthorReviewers) {
        missingTeamReviewerStages.push(name);
      }
    }
  });

  return {
    state: 'READY',
    reviewable,
    review,
    authors,
    status,
    reviewChanges,
    reviewers,
    activeReviewLifecycleVersion,
    reviewLifecycle,
    lifecycleType,
    reviewSenderId,
    hasComments,
    snapshot,
    isSelfReview,
    maybeAuthorAction,
    missingTeamReviewerStages,
  };
}
